import React from 'react';
import { Alert, Progress } from 'antd';
import { useTranslation } from 'react-i18next';
import { TRAILER_CAPACITY_LDM } from '../../../utils/transportCostAllocation';
import {
  calculatePalletSummary,
  calculatePieceMetrics,
  type ProductLogisticsInput,
} from '../productLogistics';
import { PalletDrawing } from './ProductCardDrawings';
import { formatReadout } from './cardFormat';

interface PalletSummaryPanelProps {
  values: ProductLogisticsInput;
}

const Row: React.FC<{ label: string; value: string; ok?: boolean }> = ({ label, value, ok = true }) => (
  <div className={ok ? 'product-card-eq-row' : 'product-card-eq-row is-missing'}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

/** Sticky "Jedna paleta": what one pallet means for the order and the truck. */
const PalletSummaryPanel: React.FC<PalletSummaryPanelProps> = ({ values }) => {
  const { t } = useTranslation(['products']);
  const piece = calculatePieceMetrics(values);
  const pallet = calculatePalletSummary(values);
  const packed = values.palletCountsPackages === true;
  const perPackage = Number(values.piecesPerPackage) > 0;
  const layers = pallet.pieces
    ? Math.ceil((packed ? (pallet.packages || 1) : pallet.pieces) / (packed ? 4 : 10))
    : 3;

  return (
    <aside className="product-card-section product-card-summary" aria-live="polite">
      <h3>{t('products:card.summary.title')}</h3>
      <div className="product-card-section__question">{t('products:card.summary.subtitle')}</div>
      <div className="product-card-summary__art">
        <PalletDrawing
          lengthMm={values.palletLength}
          widthMm={values.palletWidth}
          filled={Boolean(pallet.pieces)}
          layers={layers}
          label={t('products:card.summary.drawingLabel')}
        />
      </div>
      <div className="product-card-eq">
        {packed && (
          <Row label={t('products:card.summary.packages')}
            value={pallet.packages ? formatReadout(pallet.packages, 3) : t('products:card.summary.enterPackages')}
            ok={Boolean(pallet.packages)} />
        )}
        <Row label={t('products:card.summary.pieces')}
          value={pallet.pieces
            ? `${formatReadout(pallet.pieces, 3)} szt.`
            : (packed && !perPackage ? t('products:card.summary.enterPerPackage') : t('products:card.summary.enterOnPallet'))}
          ok={Boolean(pallet.pieces)} />
        {(piece.areaM2 || !pallet.pieces) && (
          <Row label={t('products:card.summary.area')}
            value={pallet.areaM2 ? `${formatReadout(pallet.areaM2, 2)} m²` : t('products:card.summary.needDimensions')}
            ok={Boolean(pallet.areaM2)} />
        )}
        {piece.volumeM3 && pallet.volumeM3 && (
          <Row label={t('products:card.summary.volume')} value={`${formatReadout(pallet.volumeM3, 3)} m³`} />
        )}
        <Row label={t('products:card.summary.gross')}
          value={pallet.grossKg ? `${formatReadout(pallet.grossKg, 0)} kg` : t('products:card.summary.needWeight')}
          ok={Boolean(pallet.grossKg)} />
        <Row label={t('products:card.summary.ldm')}
          value={pallet.ldm ? `${formatReadout(pallet.ldm, 2)} LDM` : t('products:card.summary.needSize')}
          ok={Boolean(pallet.ldm)} />
      </div>
      <div className="product-card-truck" data-testid="pallet-truck">
        {pallet.palletsPerTrailer && pallet.ldm ? (
          <>
            <div className="product-card-truck__label">{t('products:card.summary.trailer')}</div>
            <div className="product-card-truck__value">
              {t('products:card.summary.pallets', { value: formatReadout(pallet.palletsPerTrailer, 0) })}
            </div>
            <div className="product-card-truck__sub">
              {[
                pallet.pieces ? `${formatReadout(pallet.palletsPerTrailer * pallet.pieces, 0)} szt.` : null,
                pallet.areaM2 ? `${formatReadout(pallet.palletsPerTrailer * pallet.areaM2, 0)} m²` : null,
                t('products:card.summary.limit', {
                  factor: pallet.limitedBy === 'weight'
                    ? t('products:card.summary.limitWeight')
                    : t('products:card.summary.limitLength'),
                }),
              ].filter(Boolean).join(' · ')}
            </div>
            <Progress
              percent={Math.min(100, Math.round((pallet.palletsPerTrailer * pallet.ldm / TRAILER_CAPACITY_LDM) * 100))}
              showInfo={false}
              size="small"
            />
          </>
        ) : (
          <>
            <div className="product-card-truck__label">{t('products:card.summary.trailerShort')}</div>
            <div className="product-card-truck__sub">{t('products:card.summary.incomplete')}</div>
          </>
        )}
      </div>
      {pallet.warnings.map((warning) => (
        <Alert
          key={warning}
          className="product-card-warning"
          type="warning"
          showIcon
          message={t(`products:card.warnings.${warning}`, { kg: formatReadout(pallet.grossKg, 0) })}
        />
      ))}
    </aside>
  );
};

export default PalletSummaryPanel;
